<template>
  <div class="pan-search">
    <van-nav-bar title="网盘搜" fixed />

    <div class="search-header">
      <van-search
        v-model="keyword"
        placeholder="搜索网盘资源..."
        shape="round"
        background="linear-gradient(135deg, #667eea, #764ba2)"
        @search="onSearch"
      />
    </div>

    <div class="filter-bar">
      <div class="filter-scroll">
        <van-tag
          v-for="cat in typeList"
          :key="cat.value"
          :type="selectedType === cat.value ? 'primary' : 'default'"
          @click="selectType(cat.value)"
          class="filter-tag"
          :color="selectedType === cat.value ? getTypeColor(cat.value) : undefined"
        >
          {{ cat.label }}
        </van-tag>
      </div>
    </div>

    <div class="content">
      <van-loading v-if="loading" class="loading" />

      <div class="list" v-if="!loading && list.length > 0">
        <div
          v-for="item in list"
          :key="item.id"
          class="list-item"
          @click="handleDetail(item)"
        >
          <div class="item-content">
            <div class="item-title">{{ item.title }}</div>
            <div class="item-tags" v-if="item.tags && item.tags.length > 0">
              <van-tag
                v-for="tag in item.tags"
                :key="tag.id"
                size="mini"
                :color="tag.color"
                :text-color="getTagTextColor(tag.color)"
                class="tag-badge"
              >
                {{ tag.name }}
              </van-tag>
            </div>
            <div class="item-excerpt" v-if="item.excerpt">{{ item.excerpt }}</div>
            <div class="item-meta">
              <span class="item-author" v-if="item.author">
                <van-icon name="contact" size="12" /> {{ item.author }}
              </span>
              <span class="item-date" v-if="item.createdAt">{{ item.createdAt }}</span>
              <span class="item-stats" v-if="item.commentCount || item.viewCount">
                <span v-if="item.commentCount">评论 {{ item.commentCount }}</span>
                <span v-if="item.commentCount && item.viewCount"> · </span>
                <span v-if="item.viewCount">浏览 {{ item.viewCount }}</span>
              </span>
            </div>
          </div>
          <div class="item-action">
            <van-icon name="arrow" color="#ccc" />
          </div>
        </div>
      </div>

      <div class="pagination-wrapper" v-if="!loading && total > 0">
        <div class="page-info">
          共 {{ total }} 条
        </div>
        <div class="page-btns">
          <van-button size="small" :disabled="page <= 0" @click="prevPage">上一页</van-button>
          <span class="page-indicator">第 {{ page + 1 }} 页</span>
          <van-button size="small" :disabled="page >= maxPage" @click="nextPage">下一页</van-button>
        </div>
      </div>

      <div v-if="!loading && list.length === 0 && searched" class="empty">
        <van-icon name="search" size="48" color="#ddd" />
        <div>暂无搜索结果</div>
      </div>

      <div v-if="!loading && list.length === 0 && !searched" class="empty">
        <van-icon name="friends-o" size="48" color="#ddd" />
        <div>输入关键词或选择类型开始搜索</div>
      </div>
    </div>
  </div>
</template>

<script>
import { getNewPanListByType, getNewPanSearch, getUserInfo } from '../utils/api';

const TYPE_LIST = [
  { label: '全部', value: '' },
  { label: '影视', value: 'Film' },
  { label: '动漫', value: 'anime' },
  { label: '软件', value: 'Software' },
  { label: '音频', value: 'audio' },
  { label: '教学', value: 'Learn' },
  { label: '书籍', value: 'book' },
  { label: '游戏', value: 'Game' }
];

const TYPE_COLORS = {
  Film: '#1989fa',
  anime: '#f765a3',
  Software: '#07c160',
  audio: '#ff976a',
  Learn: '#7232dd',
  book: '#e6a23c',
  Game: '#e64340'
};

export default {
  name: 'PanSearch',
  data() {
    return {
      keyword: '',
      typeList: TYPE_LIST,
      selectedType: '',
      list: [],
      page: 0,
      total: 0,
      maxPage: 100,
      loading: false,
      searched: false
    };
  },
  mounted() {
    this.fetchData();
  },
  activated() {
    // 从详情页返回时恢复滚动位置
    if (this._fromDetail && this._scrollPos !== undefined) {
      this.$nextTick(() => {
        window.scrollTo(0, this._scrollPos);
        this._scrollPos = undefined;
      });
    }
    this._fromDetail = false;
  },
  beforeRouteLeave(to, from, next) {
    // 跳转到详情页时记录滚动位置
    if (to.name === 'PanDetail') {
      this._fromDetail = true;
      this._scrollPos = window.scrollY;
    }
    next();
  },
  methods: {
    getTypeColor(value) {
      return TYPE_COLORS[value] || '#1989fa';
    },
    getTagTextColor(bgColor) {
      if (!bgColor) return '#333';
      const hex = bgColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#333' : '#fff';
    },
    async fetchData() {
      this.loading = true;
      try {
        let res;
        if (this.keyword) {
          res = await getNewPanSearch({ q: this.keyword, page: this.page });
        } else if (this.selectedType) {
          res = await getNewPanListByType({ type: this.selectedType, page: this.page });
        } else {
          res = await getNewPanListByType({ type: 'Film', page: this.page });
        }
        if (res && res.data) {
          this.list = this.parseList(res);
          this.total = (res.meta && res.meta.total) || this.list.length;
          this.searched = true;
        }
      } catch (error) {
        console.error('Fetch data error:', error);
        this.$toast('加载失败');
      } finally {
        this.loading = false;
      }
    },
    parseList(response) {
      const dataArr = Array.isArray(response.data) ? response.data : [];
      const included = response.included || [];

      const tagsMap = {};
      const usersMap = {};
      included.forEach(item => {
        if (item.type === 'tags') {
          tagsMap[item.id] = {
            id: item.id,
            name: item.attributes && (item.attributes.name || item.attributes.displayName) || '',
            color: item.attributes && item.attributes.color || ''
          };
        }
        if (item.type === 'users') {
          usersMap[item.id] = item.attributes && (item.attributes.displayName || item.attributes.username) || '';
        }
      });

      return dataArr.map(item => {
        const attrs = item.attributes || {};
        const rels = item.relationships || {};

        const tags = (rels.tags && rels.tags.data || []).map(t => tagsMap[t.id]).filter(Boolean);
        const author = (rels.user && rels.user.data && usersMap[rels.user.data.id]) || '';

        let createdAt = '';
        if (attrs.createdAt) {
          const d = new Date(attrs.createdAt);
          createdAt = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }

        return {
          id: item.id,
          title: attrs.title || '',
          excerpt: attrs.excerpt || '',
          tags,
          author,
          createdAt,
          commentCount: attrs.commentCount || 0,
          viewCount: attrs.viewCount || 0
        };
      });
    },
    onSearch() {
      this.page = 0;
      this.fetchData();
    },
    selectType(value) {
      if (this.selectedType === value) return;
      this.selectedType = value;
      this.page = 0;
      this.fetchData();
    },
    prevPage() {
      if (this.page > 0) {
        this.page--;
        this.fetchData();
      }
    },
    nextPage() {
      this.page++;
      this.fetchData();
    },
    checkLogin() {
      const userinfo = localStorage.getItem('userinfo');
      if (!userinfo) {
        this.$toast('请先登录');
        setTimeout(() => {
          this.$router.push('/login');
        }, 500);
        return false;
      }
      return true;
    },
    async checkVipStatus() {
      const userinfo = localStorage.getItem('userinfo');
      if (!userinfo) return false;
      try {
        const parsed = JSON.parse(userinfo);
        if (!parsed || !parsed.data) return false;
        const userId = parsed.data.id;
        const res = await getUserInfo(userId);
        if (res && res.code === 200) {
          const userData = res.data;
          const isVip = userData.isVip === 'true';
          const vipTime = typeof userData.creatVipTime === 'string'
            ? parseInt(userData.creatVipTime) * 1000
            : userData.creatVipTime * 1000;
          const now = Date.now();
          const isExpired = vipTime ? now > vipTime : true;
          if (!isVip) {
            this.$toast('您还不是会员，请充值');
            setTimeout(() => { this.$router.push('/profile'); }, 1000);
            return false;
          }
          if (isExpired) {
            this.$toast('会员已到期，请续费');
            setTimeout(() => { this.$router.push('/profile'); }, 1000);
            return false;
          }
          return true;
        }
      } catch (error) {
        console.error('Check vip error:', error);
        this.$toast('验证失败');
        return false;
      }
      return true;
    },
    async handleDetail(item) {
      if (!this.checkLogin()) return;
      const valid = await this.checkVipStatus();
      if (!valid) return;
      this.$router.push(`/new-pan/detail/${item.id}`);
    }
  }
};
</script>

<style scoped>
.pan-search {
  min-height: 100vh;
  background: #f5f5f5;
  padding-top: 46px;
}

.search-header {
  position: sticky;
  top: 46px;
  z-index: 10;
}

.filter-bar {
  background: #fff;
  padding: 12px;
  position: sticky;
  top: 92px;
  z-index: 9;
  border-bottom: 1px solid #f0f0f0;
}

.filter-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.filter-scroll::-webkit-scrollbar {
  display: none;
}

.filter-tag {
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 13px;
}

.content {
  padding: 12px;
}

.loading {
  text-align: center;
  padding: 20px;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.list-item:active {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.item-content {
  flex: 1;
  overflow: hidden;
}

.item-title {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  margin-bottom: 8px;
}

.item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.tag-badge {
  border-radius: 8px;
}

.item-excerpt {
  font-size: 13px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 8px;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;
  flex-wrap: wrap;
}

.item-author {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.item-action {
  flex-shrink: 0;
  padding-top: 8px;
}

.pagination-wrapper {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.page-info {
  font-size: 14px;
  color: #666;
}

.page-btns {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-indicator {
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

.empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 14px;
}
</style>
